import { useState, useCallback } from 'react';
import { useTicketReopenMutation } from '../../../../../services/ticketEasyService/ticketEasy-api';
import { useMyProfileQuery } from '../../../../../services/profileService/profile-api';
import { TicketApprovalCommand } from '../../../../../services/ticketEasyService/commands/approval/TicketApprovalCommand';

interface UseTicketReopenProps {
  ticketId?: string;
  onSuccess?: () => void;
}

export function useTicketReopen({ ticketId, onSuccess }: UseTicketReopenProps) {
  const [reopening, setReopening] = useState(false);
  const [reopenSuccess, setReopenSuccess] = useState(false);
  const [reopenError, setReopenError] = useState<string | null>(null);
  
  const { data: myProfile } = useMyProfileQuery();
  const [reopenTicket] = useTicketReopenMutation();
  
  const handleReopenClick = useCallback(async () => {
    if (!ticketId) {
      setReopenError("Cannot reopen ticket: Missing ticket ID");
      return;
    }
    
    if (!myProfile) {
      setReopenError("Cannot reopen ticket: Missing user data");
      return;
    }
    
    setReopening(true);
    setReopenError(null);
    
    try {
      const reopenCommand = new TicketApprovalCommand(
        ticketId,
        myProfile.identity,
        myProfile.email,
        "Ticket reopened"
      );
      
      await reopenTicket(reopenCommand).unwrap();
      setReopenSuccess(true);
      
      if (onSuccess) {
        onSuccess();
      }
      
      // Auto-hide success message after 6 seconds
      setTimeout(() => {
        setReopenSuccess(false);
      }, 6000);
    } catch (error) {
      console.error("Failed to reopen ticket:", error);
      setReopenError("Failed to reopen ticket. Please try again.");
    } finally {
      setReopening(false);
    }
  }, [ticketId, myProfile, reopenTicket, onSuccess]);
  
  const clearSuccessMessage = useCallback(() => {
    setReopenSuccess(false);
  }, []);
  
  const clearErrorMessage = useCallback(() => {
    setReopenError(null);
  }, []);
  
  return {
    reopening,
    reopenSuccess,
    reopenError,
    handleReopenClick,
    clearSuccessMessage,
    clearErrorMessage
  };
}
